import { z } from 'zod';
import {
  KPI_AGGREGATIONS,
  type KpiAggregationName,
  type KpiDirectionName,
  type KpiPeriodicityName,
} from './kpis';
import { accumulate, kpiPerformance, plannedVsActual } from './performance';
import type { MonthPoint } from './results';
import { TRAFFIC_LIGHTS } from './status';

// ---------------------------------------------------------------------------------------------
// Previsto x Realizado (T-068, RF-26, RF-30, PRD §11.14 e §12; DEC-038)

/**
 * Métodos da projeção de fechamento (DEC-038):
 * - LINEAR: tendência linear (mínimos quadrados) dos últimos até 6 períodos com realizado; precisa de 2 ou mais.
 * - RUN_RATE: média dos períodos com realizado, repetida nos que faltam.
 * O fechamento do ano combina o realizado com o projetado pela agregação do KPI.
 */
export const PROJECTION_METHODS = ['LINEAR', 'RUN_RATE'] as const;
export type ProjectionMethodName = (typeof PROJECTION_METHODS)[number];
export const PROJECTION_METHOD_LABELS: Record<ProjectionMethodName, string> = {
  LINEAR: 'Tendência linear',
  RUN_RATE: 'Run-rate (média do realizado)',
};
/** Quantos meses recentes entram na tendência linear. */
export const LINEAR_WINDOW = 6;

const r6 = (v: number | null) => (v === null ? null : Math.round(v * 1e6) / 1e6);

const ALL_MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

/**
 * Meses em que o período do KPI fecha — os únicos que recebem realizado na série mensal (DEC-029): todos
 * (mensal, e diária, semanal e personalizada, que se somam no mês), os fins de trimestre, de semestre ou dezembro.
 */
export function periodMonthsOf(periodicity: KpiPeriodicityName): number[] {
  if (periodicity === 'QUARTERLY') return [3, 6, 9, 12];
  if (periodicity === 'SEMIANNUAL') return [6, 12];
  if (periodicity === 'ANNUAL') return [12];
  return ALL_MONTHS;
}

/**
 * Projeção do fechamento do ano a partir do realizado mensal (índice 0 = janeiro). Só os meses em que o
 * período do KPI fecha contam (\`periodMonths\`): os depois do último realizado recebem o valor projetado (na
 * tendência, o eixo é a sequência dos períodos). O fechamento combina realizado e projetado pela agregação do
 * KPI (soma; média; último valor = o do último período); \`ytd\` é o acumulado projetado mês a mês, pela mesma
 * agregação. Sem realizado suficiente, null.
 */
export function projectClosing(
  actuals: (number | null)[],
  aggregation: KpiAggregationName,
  method: ProjectionMethodName,
  periodMonths: number[] = ALL_MONTHS,
): {
  months: (number | null)[];
  ytd: (number | null)[];
  closing: number | null;
  lastActualMonth: number | null;
} {
  const realized = periodMonths
    .map((month, index) => ({ month, index, value: actuals[month - 1] ?? null }))
    .filter(
      (p): p is { month: number; index: number; value: number } =>
        p.value !== null && Number.isFinite(p.value),
    );
  const nothing = actuals.map(() => null);
  const empty = { months: nothing, ytd: nothing, closing: null, lastActualMonth: null };
  if (!realized.length) return empty;
  const last = realized.at(-1)!.month;
  let valueAt: ((index: number) => number) | null = null;
  if (method === 'RUN_RATE') {
    const mean = realized.reduce((s, p) => s + p.value, 0) / realized.length;
    valueAt = () => mean;
  } else {
    const window = realized.slice(-LINEAR_WINDOW);
    if (window.length < 2) return { ...empty, lastActualMonth: last };
    const n = window.length;
    const mx = window.reduce((s, p) => s + p.index, 0) / n;
    const my = window.reduce((s, p) => s + p.value, 0) / n;
    const sxx = window.reduce((s, p) => s + (p.index - mx) ** 2, 0);
    const slope = window.reduce((s, p) => s + (p.index - mx) * (p.value - my), 0) / sxx;
    valueAt = (index) => my + slope * (index - mx);
  }
  const months = actuals.map(() => null as number | null);
  periodMonths.forEach((month, index) => {
    if (month > last) months[month - 1] = r6(valueAt!(index));
  });
  // Valores do ano por período: o realizado até o último e o projetado depois.
  const combined = periodMonths.map((month) => (month > last ? months[month - 1]! : actuals[month - 1]!));
  const accumulateUpTo = (month: number) => {
    const values = combined.filter((_, i) => periodMonths[i]! <= month);
    return aggregation === 'LAST'
      ? (values.filter((v) => v !== null).at(-1) ?? null)
      : accumulate(values, aggregation);
  };
  const ytd = actuals.map((_, i) => (i + 1 > last ? r6(accumulateUpTo(i + 1)) : null));
  return { months, ytd, closing: r6(accumulateUpTo(12)), lastActualMonth: last };
}

/** O que a performance e a projeção precisam saber do KPI. */
export type PlannedKpi = {
  direction: KpiDirectionName;
  aggregation: KpiAggregationName;
  periodicity: KpiPeriodicityName;
  rangeLower: number | null;
  rangeUpper: number | null;
  performanceFormula: string | null;
};

const light = z.enum(TRAFFIC_LIGHTS);
export const plannedRowSchema = z.object({
  month: z.number().int(),
  target: z.number().nullable(),
  actual: z.number().nullable(),
  difference: z.number().nullable(),
  differencePercent: z.number().nullable(),
  performance: z.number().nullable(),
  light,
  ytdTarget: z.number().nullable(),
  ytdActual: z.number().nullable(),
  ytdDifference: z.number().nullable(),
  ytdDifferencePercent: z.number().nullable(),
  ytdPerformance: z.number().nullable(),
  ytdLight: light,
  /** Meta original (antes das revisões) e a performance contra ela (null sem meta original). */
  originalTarget: z.number().nullable(),
  originalPerformance: z.number().nullable(),
  originalLight: light,
  /** Projeção do mês (só nos meses depois do último realizado em que o período do KPI fecha). */
  projectedLinear: z.number().nullable(),
  projectedRunRate: z.number().nullable(),
  /** Acumulado projetado até o mês, pela agregação do KPI (realizado + projetado). */
  ytdProjectedLinear: z.number().nullable(),
  ytdProjectedRunRate: z.number().nullable(),
  /** Só para KPI de soma (RF-30): quanto da meta do ano o acumulado já consumiu e o saldo que falta. */
  ytdConsumedPercent: z.number().nullable(),
  ytdBalance: z.number().nullable(),
});
export type PlannedRow = z.infer<typeof plannedRowSchema>;

const projectionSchema = z.object({
  method: z.enum(PROJECTION_METHODS),
  closing: z.number().nullable(),
  /** Fechamento projetado contra a meta do ano. */
  closingPerformance: z.number().nullable(),
});

export const plannedSummarySchema = z.object({
  /** Meta do ano (o acumulado previsto de dezembro). */
  annualTarget: z.number().nullable(),
  /** Realizado acumulado até o último mês com realizado. */
  ytdActual: z.number().nullable(),
  lastActualMonth: z.number().int().nullable(),
  /** Só para KPI de soma (orçamento, RF-30): quanto da meta do ano já foi realizado e quanto falta. */
  consumedPercent: z.number().nullable(),
  balance: z.number().nullable(),
  projections: z.array(projectionSchema),
});
export type PlannedSummary = z.infer<typeof plannedSummarySchema>;

/**
 * Linhas do previsto x realizado e o resumo do ano a partir da série mensal (a vigente e, se houver, a da
 * meta original): diferenças, acumulado, % consumido, saldo e as projeções de fechamento.
 */
export function plannedVsActualReport(
  points: MonthPoint[],
  kpi: PlannedKpi,
  cap: number,
  original?: MonthPoint[],
): { rows: PlannedRow[]; summary: PlannedSummary } {
  const actuals = points.map((p) => p.actual);
  const periodMonths = periodMonthsOf(kpi.periodicity);
  const linear = projectClosing(actuals, kpi.aggregation, 'LINEAR', periodMonths);
  const runRate = projectClosing(actuals, kpi.aggregation, 'RUN_RATE', periodMonths);
  const annualTarget = points[11]?.ytdTarget ?? null;
  const sum = kpi.aggregation === 'SUM';
  const consumed = (ytdActual: number | null) =>
    sum && annualTarget && ytdActual !== null ? Math.round((ytdActual / annualTarget) * 10000) / 100 : null;
  const balance = (ytdActual: number | null) =>
    sum && annualTarget !== null && ytdActual !== null ? r6(annualTarget - ytdActual) : null;
  const perf = {
    direction: kpi.direction,
    rangeLower: kpi.rangeLower,
    rangeUpper: kpi.rangeUpper,
    performanceFormula: kpi.performanceFormula,
  };
  const rows = points.map((p, i) => {
    const month = plannedVsActual({ ...perf, actual: p.actual, target: p.target }, cap);
    const ytd = plannedVsActual({ ...perf, actual: p.ytdActual, target: p.ytdTarget }, cap);
    const o = original?.[i];
    return {
      month: p.month,
      target: p.target,
      actual: p.actual,
      difference: r6(month.difference),
      differencePercent: month.differencePercent,
      performance: p.performance,
      light: p.light,
      ytdTarget: p.ytdTarget,
      ytdActual: p.ytdActual,
      ytdDifference: r6(ytd.difference),
      ytdDifferencePercent: ytd.differencePercent,
      ytdPerformance: p.ytdPerformance,
      ytdLight: p.ytdLight,
      originalTarget: o?.target ?? null,
      originalPerformance: o?.performance ?? null,
      originalLight: o?.light ?? 'gray',
      projectedLinear: linear.months[i] ?? null,
      projectedRunRate: runRate.months[i] ?? null,
      ytdProjectedLinear: linear.ytd[i] ?? null,
      ytdProjectedRunRate: runRate.ytd[i] ?? null,
      ytdConsumedPercent: p.actual === null ? null : consumed(p.ytdActual),
      ytdBalance: p.actual === null ? null : balance(p.ytdActual),
    };
  });
  const last = runRate.lastActualMonth;
  const ytdActual = last ? (points[last - 1]?.ytdActual ?? null) : null;
  const closingPerformance = (closing: number | null) =>
    kpiPerformance({ ...perf, actual: closing, target: annualTarget }, cap);
  return {
    rows,
    summary: {
      annualTarget,
      ytdActual,
      lastActualMonth: last,
      consumedPercent: consumed(ytdActual),
      balance: balance(ytdActual),
      projections: [
        { method: 'LINEAR', closing: linear.closing, closingPerformance: closingPerformance(linear.closing) },
        {
          method: 'RUN_RATE',
          closing: runRate.closing,
          closingPerformance: closingPerformance(runRate.closing),
        },
      ],
    },
  };
}

// --- Contrato da API ---------------------------------------------------------------------------

const ref = z.object({ id: z.string().uuid(), name: z.string() });

/** Opções dos filtros (no escopo de quem vê). */
export const plannedOptionsSchema = z.object({
  years: z.array(z.number().int()),
  perspectives: z.array(ref),
  objectives: z.array(ref.extend({ perspectiveId: z.string().uuid() })),
  departments: z.array(ref),
  /**
   * Unidades: as da pessoa (own) e, para quem acompanha indicadores pela área, todas as da empresa — nos KPIs
   * vistos só pela unidade, a tela oferece apenas as próprias.
   */
  units: z.array(ref.extend({ own: z.boolean() })),
  kpis: z.array(
    z.object({
      id: z.string().uuid(),
      code: z.string(),
      name: z.string(),
      perspectiveId: z.string().uuid(),
      departmentId: z.string().uuid(),
      objectiveIds: z.array(z.string().uuid()),
      /** AREA: vê o consolidado e as unidades; UNIT: só as suas unidades. */
      access: z.enum(['AREA', 'UNIT']),
    }),
  ),
});
export type PlannedOptions = z.infer<typeof plannedOptionsSchema>;

export const plannedQuerySchema = z.object({
  kpiId: z.string().uuid(),
  year: z.coerce.number().int().min(2000).max(2100),
  /** Sem unidade: o consolidado da empresa. */
  unitId: z.string().uuid().optional(),
});

export const plannedReportSchema = z.object({
  kpi: z.object({
    id: z.string().uuid(),
    code: z.string(),
    name: z.string(),
    unitLabel: z.string(),
    aggregation: z.enum(KPI_AGGREGATIONS),
    direction: z.string(),
  }),
  year: z.number().int(),
  unit: ref.nullable(),
  /** Há metas revisadas no ano (a meta original difere da vigente em algum mês). */
  hasRevisions: z.boolean(),
  rows: z.array(plannedRowSchema),
  summary: plannedSummarySchema,
});
export type PlannedReport = z.infer<typeof plannedReportSchema>;
